import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Card, Empty, List, Select, Space, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { importBasket, listBaskets } from "./api";
import { formatDateTime, formatEuro } from "./format";
import { openShop } from "./SuppliersTab";

/** Draft offers a basket can go into (the legacy offer list, filtered here). */
const loadDraftOffers = async () => {
  const res = await axios.post(
    `${process.env.REACT_APP_BACKEND_URL}/quotations/filters`,
    { pageSize: 200, pageNo: 0 },
    { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
  );
  return (res.data.data || []).filter(o => o.status === "Draft");
};

/** Baskets the wholesalers' shops returned through IDS Connect. */
const BasketsTab = ({ meta, highlight }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const [baskets, setBaskets] = useState([]);
  const [offers, setOffers] = useState(null);
  const [target, setTarget] = useState({});

  const load = () =>
    listBaskets()
      .then(res => setBaskets(res.data.data))
      .catch(generalErrorAlert);

  useEffect(() => {
    load();
  }, []);

  const run = async (basket, body) => {
    try {
      const res = await importBasket(basket._id, body);
      Success(t("{{count}} positions taken over", { count: res.data.data.count }));
      load();
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const shops = meta.suppliers.filter(s => s.ids?.enabled);

  return (
    <div>
      {shops.length && meta.permissions.create ? (
        <Space wrap className="mat-section">
          {shops.map(s => (
            <Button key={s._id} onClick={() => openShop(s._id).catch(generalErrorAlert)}>
              {t("Open shop")}: {s.name}
            </Button>
          ))}
        </Space>
      ) : (
        <Typography.Paragraph className="mat-muted">
          {t("Enter the IDS Connect access of a supplier to open its shop from here.")}
        </Typography.Paragraph>
      )}
      <List
        dataSource={baskets}
        locale={{ emptyText: <Empty description={t("No baskets received yet")} /> }}
        renderItem={b => (
          <Card
            className={`mat-section${String(b._id) === highlight ? " mat-basket--highlight" : ""}`}
            title={
              <span className="mat-wrap">
                {b.supplier?.name} · {formatDateTime(b.basket?.receivedAt, lng)}
              </span>
            }
            extra={
              b.status === "imported" ? (
                <Tag color="success">{t("Taken over")}</Tag>
              ) : (
                <Tag>{t("New")}</Tag>
              )
            }
          >
            <List
              size="small"
              dataSource={b.basket?.items || []}
              renderItem={item => (
                <List.Item>
                  <span className="mat-wrap">
                    {item.quantity} {item.unit} · {item.name}{" "}
                    <span className="mat-muted">{item.articleNumber}</span>
                  </span>
                  <span className="mat-number">
                    {item.netPrice !== null && item.netPrice !== undefined
                      ? `${formatEuro(item.netPrice, lng)}${item.priceUnit > 1 ? ` / ${item.priceUnit}` : ""}`
                      : ""}
                  </span>
                </List.Item>
              )}
            />
            {b.status !== "imported" && meta.permissions.create ? (
              <Space wrap className="mat-section">
                <Button onClick={() => run(b, { target: "catalog" })}>{t("Add to catalog")}</Button>
                <Select
                  placeholder={t("Draft offer")}
                  popupMatchSelectWidth={false}
                  value={target[b._id] || b.offer || undefined}
                  onFocus={() =>
                    offers || loadDraftOffers().then(setOffers).catch(generalErrorAlert)
                  }
                  onChange={value => setTarget(prev => ({ ...prev, [b._id]: value }))}
                  options={(offers || []).map(o => ({ value: o._id, label: o.title }))}
                />
                <Button
                  type="primary"
                  disabled={!(target[b._id] || b.offer)}
                  onClick={() => run(b, { target: "offer", offer: target[b._id] || b.offer })}
                >
                  {t("Add to offer")}
                </Button>
              </Space>
            ) : null}
          </Card>
        )}
      />
    </div>
  );
};

export default BasketsTab;
